import React, { useImperativeHandle, useState, forwardRef } from 'react';
import {
  Toast,
  ToastController,
  ToastTransition,
  useColorMode,
  useColorStyle,
  Flex,
} from '@tonic-one/react';

interface ToastItem {
  appearance: 'success' | 'info' | 'warning' | 'error';
  content: React.ReactNode;
  // if undefined, not auto disappear
  duration?: number;
}

const InlineToastContainer = (props: any) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    position="absolute"
    top="12x"
    left="50%"
    transform="translateX(-50%)"
    width="max-content"
    maxWidth="80%" // up to 80% of the modal width
    zIndex="toast"
    {...props}
  />
);

/**
 * @link https://trendmicro-frontend.github.io/tonic-ui/react/v2/components/toast#drawer-with-multiple-toasts
 */
export const ModalInlineToast = forwardRef<
  {
    notify: (name: string) => void;
  },
  {
    toasts: { [name: string]: ToastItem | undefined };
  }
>(({ toasts }, ref) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [toast, setToast] = useState<ToastItem | undefined>(undefined);

  useImperativeHandle(ref, () => {
    return {
      notify: (name: string) => {
        setToast(toasts[name]);
      },
      clear: () => setToast(undefined),
    };
  }, [toasts]);

  return (
    <InlineToastContainer>
      <div>
        {toast && (
          <ToastTransition in unmountOnExit>
            <ToastController
              duration={toast.duration}
              onClose={() => setToast(undefined)}
            >
              <Toast
                appearance={toast.appearance}
                isClosable={true}
                onClose={() => setToast(undefined)}
                sx={{
                  mb: '2x',
                  minWidth: 280, // The toast has a minimum width of 280 pixels
                  width: 'fit-content',
                  boxShadow: colorStyle.shadow.thin,
                }}
              >
                {toast.content}
              </Toast>
            </ToastController>
          </ToastTransition>
        )}
      </div>
    </InlineToastContainer>
  );
});
